import { ButtonHTMLAttributes, FC, ReactNode } from "react";
import { icons, LucideIcon } from "lucide-react";

interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
  isLoading?: boolean;
  children: ReactNode;
  variant?: "primary" | "secondary" | "destructive";
  icon?: keyof typeof icons;
}

const LoadingSpinner = () => (
  <svg
    className="h-4 w-4 animate-spin text-current"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

const baseStyles =
  "cursor-pointer inline-flex items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium transition-all duration-150 focus-visible:ring-2 focus-visible:ring-soluud-primary focus-visible:ring-offset-2 focus-visible:outline-none active:scale-[0.98]";

const disabledStyles =
  "disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100";

const getVariantStyles = (
  variant: "primary" | "secondary" | "destructive" = "primary",
  isLoading: boolean,
) => {
  if (isLoading) {
    return `${baseStyles} cursor-not-allowed bg-gray-400 border-transparent text-white shadow-sm`;
  }

  switch (variant) {
    case "secondary":
      return `${baseStyles} border-soluud-primary bg-transparent text-soluud-primary hover:bg-soluud-primary-light active:bg-soluud-primary-light shadow-sm ${disabledStyles}`;
    case "destructive":
      return `${baseStyles} border-transparent bg-soluud-warning text-white hover:bg-red-600 active:bg-red-700 shadow-sm ${disabledStyles}`;
    case "primary":
    default:
      return `${baseStyles} border-transparent bg-soluud-primary text-white hover:bg-soluud-primary-hover active:bg-soluud-primary-active shadow-sm ${disabledStyles}`;
  }
};

const ActionButton: FC<ActionButtonProps> = ({
  disabled = false,
  isLoading = false,
  children,
  variant = "primary",
  className = "",
  icon,
  ...rest
}) => {
  const variantStyles = getVariantStyles(variant, isLoading);

  function getIcon(icon: keyof typeof icons) {
    const IconComponent: LucideIcon = icons[icon];
    return <IconComponent size={14} className="mr-1.5 shrink-0" />;
  }

  return (
    <button
      disabled={disabled || isLoading}
      className={`${variantStyles} ${className}`}
      {...rest}
    >
      {isLoading ? (
        <>
          <LoadingSpinner />
          <span className="ml-2">{children}</span>
        </>
      ) : (
        <>
          {icon && getIcon(icon)}
          {children}
        </>
      )}
    </button>
  );
};

export default ActionButton;
